import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const NavItem = ({ to, children, hoveredItem, setHoveredItem, layoutId }) => {
  const isHovered = hoveredItem === layoutId;

  return (
    <div
      className="relative px-5 py-2.5"
      onMouseEnter={() => setHoveredItem(layoutId)}
      onMouseLeave={() => setHoveredItem(null)} // This only clears the *highlight*, not the navbar expansion
    >
      {isHovered && (
        <motion.div
          layoutId="nav-pill"
          className="absolute inset-0 bg-black/5 rounded-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
        />
      )}
      <NavLink
        to={to}
        className={({ isActive }) =>
          `relative z-10 text-sm font-medium transition-colors ${isActive ? 'text-black font-semibold' : 'text-neutral-600 hover:text-black'}`
        }
      >
        {children}
      </NavLink>
    </div>
  );
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null); // Tracks the specific tab being hovered
  const [isNavHovered, setIsNavHovered] = useState(false); // Tracks if the mouse is ANYWHERE in the navbar

  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 20);
  });

  // Helper to determine if we should be in "Expanded/Full" mode
  // We expand if we are at the top (not scrolled) OR if the navbar is hovered
  const isExpanded = !isScrolled || isNavHovered;

  return (
    <>
      <motion.nav
        layout // Enable automatic layout animation
        className={`fixed z-50 transition-colors duration-500
            /* Mobile: Full Width Top Bar */
            top-0 left-0 right-0 h-20 bg-white/80 backdrop-blur-xl border-b border-white/20
            /* Desktop: Floating Island */
            md:top-6 md:left-0 md:right-0 md:mx-auto md:h-auto 
            md:rounded-full md:border md:border-white/40 md:shadow-2xl md:shadow-black/10 
            md:bg-white/90 md:backdrop-blur-2xl md:saturate-150 overflow-hidden text-center`}
        style={{
          willChange: "width, height, transform, opacity",
          transformOrigin: "center top"
        }}
        initial={{ y: -100, opacity: 0, width: "fit-content" }}
        animate={{
          y: 0,
          opacity: 1,
          width: "fit-content",
          // If expanded, allow wider minWidth. If collapsed (scrolled & not hovered), shrink.
          minWidth: isExpanded ? "600px" : "0",
          maxWidth: "95vw",
          padding: isExpanded ? "0.5rem" : "0",
        }}
        transition={{
          type: "tween",
          ease: [0.25, 0.1, 0.25, 1], // Even smoother cubic-bezier
          duration: 1.2, // Very slow and smooth
        }}
        onMouseEnter={() => setIsNavHovered(true)}
        onMouseLeave={() => {
          setIsNavHovered(false);
          setHoveredItem(null);
        }}
      >
        <motion.div layout className={`h-full md:py-3 px-6 md:px-8 grid items-center ${!isExpanded ? 'grid-cols-1 md:flex md:justify-center md:gap-4 md:px-2' : 'md:grid-cols-[1fr_auto_1fr] grid-cols-[auto_1fr] md:gap-12'}`}>

          {/* Logo Section */}
          <NavLink to="/" className="flex items-center gap-6 group relative z-50 shrink-0 justify-self-start md:justify-self-auto h-full">
            <motion.div layoutId="nav-logo" className="relative w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-sm border border-black/5">
              <div className="absolute inset-0 bg-blue-500/10 rounded-full animate-pulse-slow" />
              <div className="w-3 h-3 bg-[#1d1d1f] rounded-full group-hover:scale-125 transition-transform duration-500" />
            </motion.div>

            <AnimatePresence mode="wait" initial={false}>
              {isExpanded ? (
                <motion.span
                  key="expanded-logo"
                  layout
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className="text-lg font-bold tracking-tight text-[#1d1d1f] hidden lg:block overflow-hidden whitespace-nowrap pt-0.5"
                >
                  Particle Simulator
                </motion.span>
              ) : (
                <motion.span
                  key="compact-logo"
                  initial={{ opacity: 0, scale: 0.9, width: 0 }}
                  animate={{ opacity: 1, scale: 1, width: "auto" }}
                  exit={{ opacity: 0, scale: 0.9, width: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="text-sm font-bold tracking-tight text-[#1d1d1f] hidden md:block whitespace-nowrap leading-none"
                >
                  Particle Simulator
                </motion.span>
              )}
            </AnimatePresence>

            <span className="text-lg font-bold tracking-tight text-[#1d1d1f] lg:hidden md:hidden">
              PS
            </span>
          </NavLink>

          {/* Desktop Nav - Perfectly Centered in Grid (Center) */}
          <div className="hidden md:flex items-center justify-center">
            <AnimatePresence mode="popLayout">
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, filter: "blur(4px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 0.9, width: 0, filter: "blur(4px)" }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                  className="flex items-center gap-1 bg-neutral-100/50 rounded-full p-1.5 border border-black/5 overflow-hidden"
                >
                  <NavItem to="/" hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} layoutId="home">Home</NavItem>
                  <NavItem to="/simulator" hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} layoutId="simulator">Simulator</NavItem>
                  <NavItem to="/learn" hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} layoutId="learn">Learn</NavItem>
                  <NavItem to="/team" hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} layoutId="team">Team</NavItem>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Utilities - Right Side (Right) */}
          <div className="hidden md:flex items-center gap-4 shrink-0 justify-self-end md:justify-self-auto">
            <AnimatePresence mode="popLayout">
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, filter: "blur(4px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 0.9, width: 0, filter: "blur(4px)" }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                >
                  <NavLink
                    to="/simulator"
                    className="px-8 py-3.5 bg-[#1d1d1f] hover:bg-black text-white text-base font-semibold rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-black/20 whitespace-nowrap block"
                  >
                    Launch App
                  </NavLink>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Mobile Toggle */}
          <button
            className="md:hidden p-2 text-slate-800 z-50 relative justify-self-end"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </motion.div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white/90 backdrop-blur-3xl pt-28 px-8 md:hidden flex flex-col"
          >
            <div className="space-y-6">
              {[
                { to: "/", label: "Home" },
                { to: "/simulator", label: "Simulator" },
                { to: "/learn", label: "Learn" },
                { to: "/team", label: "Team" },
              ].map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 + 0.1, duration: 0.5, type: "spring", bounce: 0.3 }}
                >
                  <NavLink
                    to={item.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) => `block text-4xl font-semibold tracking-tighter transition-colors ${isActive ? 'text-black' : 'text-neutral-400'}`}
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-auto mb-12"
            >
              <NavLink
                to="/simulator"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full py-5 bg-black text-white text-xl text-center font-bold rounded-3xl"
              >
                Launch App
              </NavLink>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
